import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'

import useLockBodyScroll from '@/shared/hooks/useLockScroll'
import useMediaQuery from '@/shared/hooks/useMediaQuery'
import usePageMatch from '@/shared/hooks/usePageMatch'
import { Nanum_b_size } from '@/styles/font/NanumSquare'
import { mediaTablet } from '@/styles/mediaQuery'

const NavBar = () => {
  const [isOpen, setIsOpen] = useState(false)
  const { isTermsPage, isNewsPage, isFaqPage, isCenterPage, isRefundPage } = usePageMatch()
  const { isMobile } = useMediaQuery()

  const isLockScroll = isOpen && isMobile
  useLockBodyScroll(isLockScroll)

  return (
    <>
      <ToggleButton $isOpen={isOpen} onClick={() => setIsOpen((prev) => !prev)}>
        <Bar $isOpen={isOpen} index={1} />
        <Bar $isOpen={isOpen} index={2} />
        <Bar $isOpen={isOpen} index={3} />
      </ToggleButton>
      <NavBarWrap $isOpen={isOpen}>
        <MenuList $isOpen={isOpen}>
          {[
            { to: '/home', label: 'HOME' },
            { to: '/intro', label: '서비스 소개' },
            { to: '/notice', label: '고객지원' },
            { to: '/cancel', label: '서비스 해지' },
            { to: '/terms/10/1', label: '이용약관' },
          ].map(({ to, label }) => {
            const className = (label: string) => {
              if (label === '이용약관' && isTermsPage) return 'active'
              if (label === '고객지원' && (isNewsPage || isFaqPage || isCenterPage || isRefundPage))
                return 'active'
              return ''
            }
            return (
              <MenuListItem
                key={to}
                to={to}
                className={className(label)}
                onClick={() => setIsOpen(false)}
              >
                {label}
              </MenuListItem>
            )
          })}
        </MenuList>
        <Blur $isOpen={isOpen} onClick={() => setIsOpen(false)} />
      </NavBarWrap>
    </>
  )
}

export default NavBar

const ToggleButton = styled.button<{ $isOpen: boolean }>`
  z-index: 1000;
  width: 48px;
  height: 48px;
  background-color: var(--white);
  border-radius: 6px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  position: relative;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  ${mediaTablet} {
    width: 32px;
    height: 32px;
  }
`

const Bar = styled.span<{ $isOpen: boolean; index: number }>`
  position: absolute;
  width: 24px;
  height: 2px;
  background-color: var(--black);
  border-radius: 4px;
  left: 50%;
  transform: translateX(-50%);
  transition: all 0.3s ease-in-out;

  ${({ index }) => (index === 1 ? `top: 13px;` : index === 2 ? `top: 23px;` : `top: 34px;`)}
  ${({ $isOpen, index }) =>
    $isOpen &&
    (index === 1
      ? `transform: translateX(-50%) rotate(45deg); top: 22px;`
      : index === 2
        ? `opacity: 0;`
        : `transform: translateX(-50%) rotate(-45deg); top: 22px;`)}

  ${mediaTablet} {
    width: 16px;
    ${({ index }) => (index === 1 ? `top: 9px;` : index === 2 ? `top: 15px;` : `top: 21px;`)}

    ${({ $isOpen, index }) =>
      $isOpen &&
      (index === 1
        ? `transform: translateX(-50%) rotate(45deg); top: 16px;`
        : index === 2
          ? `opacity: 0;`
          : `transform: translateX(-50%) rotate(-45deg); top: 16px;`)}
  }
`

const NavBarWrap = styled.nav<{ $isOpen: boolean }>`
  position: fixed;
  width: 100%;
  height: 100vh;
  overflow-y: hidden;
  inset: 0;
  z-index: 10;
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transition: visibility 0.4s ease-in-out;
`

const MenuList = styled.menu<{ $isOpen: boolean }>`
  background-color: var(--white);
  z-index: 10;
  width: 400px;
  height: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
  gap: 32px;
  top: 0;
  right: ${({ $isOpen }) => ($isOpen ? '0' : '-400px')};
  padding: 90px 24px;
  transition: right 0.3s ease-in-out;
  ${Nanum_b_size(24)};

  ${mediaTablet} {
    width: 360px;
    right: ${({ $isOpen }) => ($isOpen ? '0' : '-360px')};
  }
`

const Blur = styled.div<{ $isOpen: boolean }>`
  background-color: rgba(0, 0, 0, 0.6);
  position: absolute;
  inset: 0;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transition: opacity 0.4s ease-in-out;
`

const MenuListItem = styled(NavLink)`
  ${Nanum_b_size(24)};
  ${mediaTablet} {
    ${Nanum_b_size(20)};
  }
  &.active {
    color: var(--green);
  }
`
